import { useAuth } from './auth-provider';
import { useCallback } from 'react';

export const usePermissions = () => {
    const {
        permissions,
        roles,
        hasPermission,
        hasAnyPermission,
        hasAllPermissions,
        hasRole,
        hasAnyRole,
        hasAllRoles,
        canAccess,
        canPerform,
        getPermissionsByModule,
        // Role helpers
        isSuperAdmin,
        isAdmin,
        isManager,
        isOperator,
        isFieldWorker,
        isViewer,
        // Job helpers
        canAccessAssignedJobs,
        shouldSeeOnlyOwnJobs,
        // PKGP permissions
        canCreatePkgp,
        canUpdatePkgp,
        canDeletePkgp,
        canStartPkgp,
        canEndPkgp,
        canPdfPkgp,
        // PUGI permissions
        canCreatePugi,
        canUpdatePugi,
        canDeletePugi,
        canStartPugi,
        canEndPugi,
        canPdfPugi,
        // ZM permissions
        canCreateZm,
        canUpdateZm,
        canDeleteZm,
        canStartZm,
        canEndZm,
        canPdfZm,
        // User management
        canManageUsers,
        canCreateUsers,
        canUpdateUsers,
        canDeleteUsers,
        // Role/Permission management
        canManageRoles,
        canManagePermissions,
        // System data
        canManageArticles,
        canManageMeasuringPoints
    } = useAuth();

    // Wrapper functions for easier usage
    const checkPermission = useCallback(
        (permission) => {
            return hasPermission(permission);
        },
        [hasPermission]
    );

    const checkAnyPermission = useCallback(
        (permissionArray) => {
            return hasAnyPermission(permissionArray);
        },
        [hasAnyPermission]
    );

    const checkAllPermissions = useCallback(
        (permissionArray) => {
            return hasAllPermissions(permissionArray);
        },
        [hasAllPermissions]
    );

    const checkRole = useCallback(
        (role) => {
            return hasRole(role);
        },
        [hasRole]
    );

    const checkAnyRole = useCallback(
        (roleArray) => {
            return hasAnyRole(roleArray);
        },
        [hasAnyRole]
    );

    const checkAllRoles = useCallback(
        (roleArray) => {
            return hasAllRoles(roleArray);
        },
        [hasAllRoles]
    );

    const checkModuleAccess = useCallback(
        (module, action = 'view') => {
            return action === 'view' ? canAccess(module) : canPerform(module, action);
        },
        [canAccess, canPerform]
    );

    // Utility function to check combined conditions
    const checkAccess = useCallback(
        ({ permission, permissions, role, roles, module, action = 'view', requireAll = false }) => {
            // Check single permission
            if (permission && !hasPermission(permission)) {
                return false;
            }

            // Check multiple permissions
            if (permissions && Array.isArray(permissions)) {
                const hasAccess = requireAll ? hasAllPermissions(permissions) : hasAnyPermission(permissions);

                if (!hasAccess) return false;
            }

            // Check single role
            if (role && !hasRole(role)) {
                return false;
            }

            // Check multiple roles
            if (roles && Array.isArray(roles)) {
                const hasAccess = requireAll ? hasAllRoles(roles) : hasAnyRole(roles);

                if (!hasAccess) return false;
            }

            // Check module access
            if (module) {
                const hasAccess = action === 'view' ? canAccess(module) : canPerform(module, action);

                if (!hasAccess) return false;
            }

            return true;
        },
        [hasPermission, hasAnyPermission, hasAllPermissions, hasRole, hasAnyRole, hasAllRoles, canAccess, canPerform]
    );

    return {
        // Direct access to permissions and roles
        permissions,
        roles,

        // Permission checking functions
        hasPermission: checkPermission,
        hasAnyPermission: checkAnyPermission,
        hasAllPermissions: checkAllPermissions,
        checkPermission,
        checkAnyPermission,
        checkAllPermissions,

        // Role checking functions
        hasRole: checkRole,
        hasAnyRole: checkAnyRole,
        hasAllRoles: checkAllRoles,
        checkRole,
        checkAnyRole,
        checkAllRoles,

        // Module access functions
        canAccess,
        canPerform,
        checkModuleAccess,

        // Combined access checker
        checkAccess,

        // Utility functions
        getPermissionsByModule,

        // Quick access helpers
        can: checkPermission,
        cannot: (permission) => !checkPermission(permission),
        is: checkRole,
        isNot: (role) => !checkRole(role),

        // Role helpers
        isSuperAdmin,
        isAdmin,
        isManager,
        isOperator,
        isFieldWorker,
        isViewer,

        // Job helpers
        canAccessAssignedJobs,
        shouldSeeOnlyOwnJobs,

        // Module-specific permissions
        canCreatePkgp,
        canUpdatePkgp,
        canDeletePkgp,
        canStartPkgp,
        canEndPkgp,
        canPdfPkgp,
        canCreatePugi,
        canUpdatePugi,
        canDeletePugi,
        canStartPugi,
        canEndPugi,
        canPdfPugi,
        canCreateZm,
        canUpdateZm,
        canDeleteZm,
        canStartZm,
        canEndZm,
        canPdfZm,
        canManageUsers,
        canCreateUsers,
        canUpdateUsers,
        canDeleteUsers,
        canManageRoles,
        canManagePermissions,
        canManageArticles,
        canManageMeasuringPoints
    };
};

export default usePermissions;
